import { useMutation } from '@tanstack/react-query'

import { parseApiError } from '@/src/general/utilities/fetcher/parseApiError'

import { authApi } from '../api/authApi'
import type {
  TOtpInitializationPayload,
  TOtpLoginPayload,
  TOtpSignupPayload,
} from '../api/types/otpAuthentication.types'
import { useConnectSuccess } from './useConnectSuccess'

export type TOtpAuthenticationMode = 'login' | 'signup'

type TOtpInitialization = Omit<TOtpInitializationPayload, 'csrf_token'> & { mode: TOtpAuthenticationMode }
type TOtpAuthenticationPayload = TOtpLoginPayload & { mode: TOtpAuthenticationMode; name?: string }

export const useOtpAuthentication = (redirectTo?: string) => {
  const onSuccess = useConnectSuccess(redirectTo)

  const loginMethodsMutation = useMutation({
    mutationFn: (email: string) => authApi.getLoginMethods(email, {}),
  })

  const initializationMutation = useMutation({
    mutationFn: async ({ mode, email }: TOtpInitialization) => {
      const { csrf_token: csrfToken } =
        mode === 'login' ? await authApi.getOtpLoginCsrf() : await authApi.getOtpSignupCsrf()
      const payload = { csrf_token: csrfToken, email }
      if (mode === 'login') await authApi.initializeOtpLogin(payload)
      else await authApi.initializeOtpSignup(payload)
      return csrfToken
    },
  })

  const authenticationMutation = useMutation({
    mutationFn: (payload: TOtpAuthenticationPayload) => {
      if (payload.mode === 'login') {
        const loginPayload: TOtpLoginPayload = {
          csrf_token: payload.csrf_token,
          email: payload.email,
          code: payload.code,
        }
        return authApi.postOtpLogin(loginPayload)
      }

      const signupPayload: TOtpSignupPayload = {
        csrf_token: payload.csrf_token,
        email: payload.email,
        code: payload.code,
        name: payload.name || '',
      }
      return authApi.postOtpSignup(signupPayload)
    },
    onSuccess,
  })

  const loginMethodsEmailErrorKey = parseApiError(loginMethodsMutation.error, 'email')
  const initializationEmailErrorKey = parseApiError(initializationMutation.error, 'email')
  const authenticationCodeErrorKey =
    parseApiError(authenticationMutation.error, 'code') || parseApiError(authenticationMutation.error, 'login')
  const authenticationNameErrorKey = parseApiError(authenticationMutation.error, 'name')
  const authenticationCsrfErrorKey = parseApiError(authenticationMutation.error, 'csrf_token')

  const authenticateOtp = async (payload: TOtpAuthenticationPayload) => {
    try {
      await authenticationMutation.mutateAsync(payload)
      return { status: 'authenticated' as const }
    } catch (error) {
      const nameKey = parseApiError(error, 'name')
      if (nameKey) return { status: 'name-error' as const, nameKey }
      if (!parseApiError(error, 'csrf_token')) throw error

      const csrfToken = await initializationMutation.mutateAsync({ mode: payload.mode, email: payload.email })
      return { status: 'csrf-refreshed' as const, csrfToken }
    }
  }

  return {
    loginMethodsMutation,
    initializationMutation,
    authenticationMutation,
    authenticateOtp,
    errors: {
      loginMethods: {
        emailKey: loginMethodsEmailErrorKey,
        generalKey:
          loginMethodsMutation.error && !loginMethodsEmailErrorKey
            ? parseApiError(loginMethodsMutation.error) || 'common.form.error.fail'
            : undefined,
      },
      initialization: {
        emailKey: initializationEmailErrorKey,
        generalKey:
          initializationMutation.error && !initializationEmailErrorKey
            ? parseApiError(initializationMutation.error) || 'common.form.error.fail'
            : undefined,
      },
      authentication: {
        codeKey: authenticationCodeErrorKey,
        nameKey: authenticationNameErrorKey,
        generalKey:
          authenticationMutation.error &&
          !authenticationCodeErrorKey &&
          !authenticationNameErrorKey &&
          !authenticationCsrfErrorKey
            ? parseApiError(authenticationMutation.error) || 'common.form.error.fail'
            : undefined,
      },
    },
  }
}
